import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Cable, CheckCircle2, Copy, ArrowRight, ChevronRight,
  RefreshCw, Cpu, BadgeCheck, ArrowUpRight, Layers, Car,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, PortalButton } from "@/components/Motion";
import { API, PORTAL_URL, SITE_ORIGIN, money, setJsonLd, removeJsonLd } from "@/lib/site";

// The six current FLEX configurations — pulled live from the database, never recreated here.
const FLEX_GROUPS = [
  { id: "flex-regal-slave", label: "Regal Tuning Slaves", slugs: ["flex-slave-obd-bench-regal", "flex-slave-full-regal"] },
  { id: "flex-any-master", label: "Slave to Any Master", slugs: ["flex-slave-obd-bench-any", "flex-slave-full-any"] },
  { id: "flex-master-options", label: "Master", slugs: ["flex-master-obd-bench", "flex-master-full"] },
];
const FLEX_SLUGS = FLEX_GROUPS.flatMap((g) => g.slugs);

const FEATURES = [
  [Cable, "OBD, Bench & Boot", "ECU and TCU reading and writing through the diagnostic port, on the bench, or in boot mode — depending on the selected software package and the supported control unit."],
  [Cpu, "ECU & TCU Programming", "One platform for both engine and transmission control units, with protocol coverage that is continuously expanding."],
  [Layers, "Full Backup Where Supported", "Complete backup of the control unit's data on supported ECUs and TCUs before any changes are made."],
  [Copy, "Cloning Where Supported", "ECU and TCU cloning operations on supported units — useful for replacement and recovery work."],
  [CheckCircle2, "Checksum Calculation", "Checksum calculation handled by the software on supported families during the writing process."],
  [RefreshCw, "Expanding Coverage", "Magicmotorsport releases new protocols and vehicle support continuously across cars, vans, motorcycles and heavy-duty applications — depending on your licence package."],
];

const METHODS = [
  ["OBD", "Programming through the vehicle's diagnostic connection on supported vehicles. The fastest method for day-to-day tuning work — no need to remove the control unit."],
  ["Bench", "Direct communication with the ECU or TCU on the bench — outside the vehicle or through a bench connection, depending on the protocol — typically without opening the unit."],
  ["Boot", "Low-level ECU/TCU access used on supported control units for programming, full backup, recovery and other advanced operations."],
];

const MANUFACTURERS = ["Bosch", "Continental", "Siemens/Continental", "Delphi", "Denso", "Marelli", "ZF"];

const WHY_REGAL = [
  "Professional ECU tuning specialists — not a box-shifting reseller",
  "Genuine Magicmotorsport equipment, supplied configured and ready to work",
  "Master and Slave configurations to suit your business model",
  "FLEX Slave options linked directly to Regal Tuning's file service",
  "In-house ECU tuning files, developed and verified on our 4WD dyno",
  "Technical support from tuners with 20+ years of industry experience",
];

const FAQS = [
  ["What is Magicmotorsport FLEX?", "Magicmotorsport FLEX is a professional ECU and TCU programming platform used for vehicle tuning and control-unit work. Depending on the software package you select and the control unit involved, FLEX supports OBD, Bench and Boot programming methods across cars, vans, motorcycles and heavy-duty applications."],
  ["What is the difference between FLEX Master and FLEX Slave?", "The hardware is the same — the difference is the software licence. A FLEX Master works with plaintext/.bin files where supported, giving you full control over your tuning files, and can build its own network of linked Slave users. A FLEX Slave reads and writes encrypted files that are processed through the Master file provider the tool is linked to."],
  ["Should I buy FLEX Master or FLEX Slave?", "Choose Master if you want to control your own tuning files — editing them yourself or using any file provider — or if you plan to build a Slave network. Choose Slave if you want to work with a chosen Master file provider who handles file modification for you. If you're unsure, talk to us before ordering and we'll recommend the right route for your business."],
  ["Can FLEX program both ECUs and TCUs?", "Yes. FLEX covers both engine control units (ECU) and transmission control units (TCU), subject to protocol coverage for the specific unit and the software package on your licence."],
  ["Does FLEX support OBD, Bench and Boot?", "Yes, all three methods are part of the FLEX platform — but not every control unit supports every method. Which method applies depends on the specific ECU or TCU and protocol, so coverage should always be checked for the unit you need to work on."],
  ["Can FLEX clone an ECU or TCU?", "FLEX supports cloning operations on a range of ECUs and TCUs where the protocol allows it. Cloning is not universal — confirm support for your specific control unit on the official Magicmotorsport vehicle list before purchase."],
  ["What vehicles does Magicmotorsport FLEX support?", "FLEX has a continually expanding compatibility database covering cars, vans, motorcycles and heavy-duty applications, across major control-unit manufacturers such as Bosch, Continental, Delphi, Denso, Marelli and ZF. Because support is updated constantly, always check the exact vehicle, ECU or TCU you need before buying."],
  ["What happens if I buy a FLEX Slave linked to Regal Tuning?", "Your FLEX Slave is supplied linked to Regal Tuning as your Master file provider. You read the vehicle with your tool, submit the file to us, and receive a professionally developed calibration back through our tuning file service — backed by our technical support team."],
  ["Can a FLEX Slave be linked to another Master?", "Yes — we supply FLEX Slave packages linked to Regal Tuning, and 'Any Master' variants for customers who already have a chosen Master provider. Linking or moving a Slave between Masters is handled through the Master providers involved — contact us and we'll advise on your situation."],
  ["Can I upgrade my FLEX software later?", "Yes. FLEX software packages are expandable — you can start with an OBD + Bench package and add further protocol coverage later. Upgrade options and pricing vary by package, so contact our team for a quote on the coverage you need."],
  ["Do I need an active subscription?", "It depends on the package. FLEX update subscriptions are available in our range, and what is included varies by package and can change over time — check the details of the specific product you are buying or ask us before ordering."],
  ["Where can I check FLEX vehicle compatibility?", "Use the official Magicmotorsport FLEX vehicle list — a live, searchable database of supported vehicles, ECUs and TCUs. If you'd like a second opinion, send us the vehicle and control-unit details and we'll confirm coverage before you buy."],
];

// Full FLEX SEO content (hero through FAQ) — rendered standalone on
// /magicmotorsport-flex and embedded at the top of /tuning-tools/magic-motorsport.
export function MagicFlexContent({ embedded = false } = {}) {
  useEffect(() => {
    // canonical is managed globally by ScrollManager in App.js
    setJsonLd("ld-breadcrumb", {
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN + "/" },
        { "@type": "ListItem", position: 2, name: "Tuning Tools", item: SITE_ORIGIN + "/tuning-tools" },
        { "@type": "ListItem", position: 3, name: "Magicmotorsport FLEX", item: SITE_ORIGIN + "/magicmotorsport-flex" },
      ],
    });
    setJsonLd("ld-faq", {
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: FAQS.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    });
    return () => { removeJsonLd("ld-breadcrumb"); removeJsonLd("ld-faq"); };
  }, []);

  // Standalone page only: the six FLEX configurations (the category page shows
  // its own Popular FLEX Configurations grid below the embedded content).
  const [products, setProducts] = useState([]);
  useEffect(() => {
    if (embedded) return;
    axios
      .get(`${API}/products?category=magic-motorsport&limit=50`)
      .then((r) => {
        const bySlug = Object.fromEntries(r.data.map((p) => [p.slug, p]));
        setProducts(FLEX_SLUGS.map((s) => bySlug[s]).filter(Boolean));
      })
      .catch(() => {});
  }, [embedded]);

  return (
    <>
      {/* HERO */}
      <section className={`relative overflow-hidden ${embedded ? "pb-20 sm:pb-24" : "pt-36 pb-20 sm:pt-44 sm:pb-24"}`} data-testid="magicflex-hero">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="container-regal relative">
          <nav className="mb-8 flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500" aria-label="Breadcrumb" data-testid="magicflex-breadcrumbs">
            <Link to="/" className="hover:text-regal-amber">Home</Link>
            <ChevronRight size={12} />
            <Link to="/tuning-tools" className="hover:text-regal-amber">Tuning Tools</Link>
            <ChevronRight size={12} />
            <span className="text-neutral-300">Magicmotorsport FLEX</span>
          </nav>
          <Reveal><p className="overline-label mb-4">Genuine Magicmotorsport Equipment</p></Reveal>
          <Reveal delay={0.08}>
            <h1 className="max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase leading-[1.0] tracking-tight text-ink">
              Magicmotorsport FLEX ECU &amp; TCU Programming Tool
            </h1>
          </Reveal>
          <Reveal delay={0.16} className="mt-7 max-w-2xl">
            <p className="text-base sm:text-lg text-neutral-400 leading-relaxed">
              FLEX is a professional ECU and TCU programming platform for vehicle tuning and electronic control-unit work — with OBD, Bench and Boot programming capabilities depending on the selected software package and supported control unit. Available as a Master tool or as a Slave linked to your chosen file provider.
            </p>
          </Reveal>
          <Reveal delay={0.24} className="mt-9 flex flex-wrap gap-4">
            <a href="#flex-products" data-testid="magicflex-shop-cta" className="inline-flex items-center gap-2 bg-regal-amber px-9 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
              Shop FLEX Tools <ArrowRight size={15} />
            </a>
            <a href="#flex-options" data-testid="magicflex-options-cta" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
              View FLEX Options
            </a>
          </Reveal>
        </div>
      </section>

      {/* WHAT IS FLEX */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="magicflex-what">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">What is Magicmotorsport FLEX?</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-6 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">One platform for ECU &amp; TCU work</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
              FLEX reads and writes engine and transmission control units across a wide and constantly growing range of vehicles — cars, vans, motorcycles and heavy-duty applications depending on your licence package. Exact functions and vehicle coverage always depend on the specific ECU or TCU, the protocol, and the FLEX software package you select.
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="h-full border border-line bg-white p-7 shadow-[0_1px_2px_rgba(16,24,40,0.05)]">
                  <Icon size={22} className="text-regal-amber" strokeWidth={1.5} />
                  <h3 className="mt-4 font-display text-base font-bold uppercase tracking-wide text-ink">{t}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 flex items-start gap-3 border border-line bg-white p-5 text-sm text-neutral-400" data-testid="magicflex-coverage-note">
              <Car size={16} className="mt-0.5 shrink-0 text-regal-amber" />
              Coverage note: not every vehicle supports every programming mode. Always confirm the exact vehicle, ECU or TCU you need before purchase — or ask our team and we'll check for you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MASTER VS SLAVE */}
      <section id="flex-options" className="border-t border-line py-20 sm:py-24 scroll-mt-24" data-testid="magicflex-compare">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">FLEX Master vs FLEX Slave</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-12 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Choose the licence that fits your business</h2></Reveal>
          <div className="grid gap-6 lg:grid-cols-2">
            <Reveal>
              <div className="flex h-full flex-col border border-line bg-white p-8 sm:p-10 shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid="compare-master-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">Independence</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">FLEX Master</h3>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-neutral-400">
                  FLEX Master is intended for professional tuners who want control over their tuning files. Master users can work with plaintext/.bin files where supported — edit them yourself with compatible calibration software, or send them to any file provider you choose.
                </p>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-neutral-400">
                  A FLEX Master can also build its own Slave network: Magicmotorsport currently allows up to 99 linked Slave users per Master — ideal for growing a dealer or agent network.
                </p>
                <div className="mt-auto pt-8">
                  <Link to="/build-your-flex/master" data-testid="view-flex-master" className="inline-flex items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
                    View FLEX Master Options <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="flex h-full flex-col border border-line bg-white p-8 sm:p-10 shadow-[0_1px_2px_rgba(16,24,40,0.05)]" data-testid="compare-slave-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">Supported</p>
                <h3 className="mt-2 font-display text-2xl font-extrabold uppercase tracking-tight text-ink">FLEX Slave</h3>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-neutral-400">
                  FLEX Slave is designed for workshops and tuners who want to work with a chosen Master file provider. Slave files are encrypted, and file modification is handled through the linked Master — you read and write the vehicle, your Master supplies the calibration.
                </p>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-neutral-400">
                  Choose a FLEX Slave linked to Regal Tuning and you combine the FLEX hardware with our professional <Link to="/tuning-files" className="text-regal-amber underline-offset-4 hover:underline">ECU tuning file service</Link> and technical support — developed in-house and verified on our 4WD dyno.
                </p>
                <div className="mt-auto pt-8">
                  <a href="#flex-regal-slave" data-testid="view-regal-flex-slave" className="inline-flex items-center gap-2 border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors">
                    View Regal FLEX Slave Options <ArrowRight size={15} />
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OBD / BENCH / BOOT */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="magicflex-methods">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">Programming Methods</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-5 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">OBD, Bench &amp; Boot explained</h2></Reveal>
          <Reveal delay={0.1}><p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">Three ways to reach the control unit. Which method applies depends on the specific ECU or TCU and protocol — not every unit supports every method.</p></Reveal>
          <div className="grid gap-px border border-line bg-line sm:grid-cols-3">
            {METHODS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.07} className="bg-white">
                <div className="h-full p-8">
                  <p className="font-mono text-xs tracking-[0.3em] text-regal-amber">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink">{t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-neutral-400">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="magicflex-coverage">
        <div className="container-regal grid items-center gap-12 lg:grid-cols-2">
          <div>
            <Reveal><p className="overline-label mb-4">Vehicle &amp; Control-Unit Coverage</p></Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">An expanding compatibility database</h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-5 text-sm sm:text-base text-neutral-400 leading-relaxed">
                FLEX supports a wide range of ECU and TCU systems from the major control-unit manufacturers — including {MANUFACTURERS.slice(0, -1).join(", ")} and {MANUFACTURERS[MANUFACTURERS.length - 1]} — and Magicmotorsport adds new protocols continuously.
              </p>
              <p className="mt-4 text-sm sm:text-base text-neutral-400 leading-relaxed">
                Because support is updated all the time, we don't publish a static list here: check the exact vehicle, ECU or TCU you need on the official Magicmotorsport database before purchase, or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">ask our team</Link> to confirm coverage for you.
              </p>
            </Reveal>
            <Reveal delay={0.18} className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/flex-vehicle-list" data-testid="magicflex-compat-cta" className="inline-flex items-center gap-2 bg-regal-amber px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.14em] text-white hover:bg-amber-400 transition-colors">
                Check FLEX Compatibility <ArrowRight size={15} />
              </Link>
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-neutral-500">Search the full FLEX vehicle database without leaving Regal Tuning</span>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <div className="overflow-hidden border border-line">
              <img src="/assets/workshop/dyno-turbos.webp" alt="Porsche Turbo S on the Regal Tuning 4WD dyno during ECU calibration development" loading="lazy" className="aspect-[4/3] w-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY REGAL */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="magicflex-why-regal">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">Why Buy FLEX From Regal Tuning?</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Hardware plus real tuning support</h2></Reveal>
          <div className="grid gap-3 sm:grid-cols-2">
            {WHY_REGAL.map((a, i) => (
              <Reveal key={a} delay={i * 0.04}>
                <p className="flex items-center gap-3 border border-line bg-white p-4 text-sm font-medium text-ink">
                  <BadgeCheck size={16} className="shrink-0 text-regal-amber" /> {a}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 text-sm leading-relaxed text-neutral-400">
              New to professional tuning? Our <Link to="/become-a-dealer" className="text-regal-amber underline-offset-4 hover:underline">dealer programme</Link> combines equipment, tuning files and ongoing technical support to help workshops add ECU tuning properly.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FLEX PRODUCTS — standalone page only */}
      {!embedded && (
      <section id="flex-products" className="border-t border-line py-20 sm:py-24 scroll-mt-24" data-testid="magicflex-products">
        <div className="container-regal">
          <Reveal><p className="overline-label mb-4">FLEX Products</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-6 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Current FLEX configurations</h2></Reveal>
          <Reveal delay={0.1}>
            <p className="mb-12 max-w-2xl text-sm sm:text-base text-neutral-400 leading-relaxed">
              Master and Slave packages, with Slave options linked to Regal Tuning or to your own chosen Master. Need a specific protocol combination? Configure it with our <Link to="/build-your-flex/master" className="text-regal-amber underline-offset-4 hover:underline">Build Your FLEX tool</Link>.
            </p>
          </Reveal>
          {products.length > 0 ? (
            <div data-testid="magicflex-product-grid">
              {FLEX_GROUPS.map((g) => {
                const items = g.slugs.map((s) => products.find((p) => p.slug === s)).filter(Boolean);
                if (!items.length) return null;
                return (
                  <div key={g.id} id={g.id} className="mb-10 scroll-mt-32" data-testid={`flex-group-${g.id}`}>
                    <Reveal><p className="mb-4 font-mono text-[11px] uppercase tracking-[0.24em] text-regal-amber">{g.label}</p></Reveal>
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {items.map((p, i) => (
                        <Reveal key={p.slug} delay={i * 0.05}>
                          <Link to={`/product/${p.slug}`} data-testid={`magicflex-product-${p.slug}`} className="group flex h-full flex-col border border-line bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] hover:border-regal-amber transition-colors">
                            <div className="border-b border-line bg-surface p-8">
                              <img src={p.images?.[0] || "/assets/brands/magic-motorsport.webp"} alt={`${p.name} — Magicmotorsport FLEX programming tool`} loading="lazy" className="mx-auto h-24 object-contain" />
                            </div>
                            <div className="flex flex-1 flex-col p-6">
                              <h3 className="font-display text-sm font-bold uppercase tracking-wide text-ink group-hover:text-regal-amber transition-colors">{p.name}</h3>
                              <p className="mt-auto pt-4 font-display text-lg font-extrabold text-ink">{money(p.sale_price || p.price_ex_vat, p.currency)}</p>
                              <span className="mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-regal-amber">
                                View product <ArrowRight size={12} />
                              </span>
                            </div>
                          </Link>
                        </Reveal>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="border border-dashed border-linebright p-8 text-sm text-neutral-400" data-testid="magicflex-products-pending">
              FLEX products are listed in our <Link to="/tuning-tools" className="text-regal-amber underline-offset-4 hover:underline">tuning tools shop</Link>.
            </p>
          )}
        </div>
      </section>
      )}

      {/* FILE SERVICE */}
      <section className="border-t border-line bg-surface py-20 sm:py-24" data-testid="magicflex-file-support">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">Regal Tuning File Service</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="mb-6 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Pair FLEX with professional tuning files</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-sm sm:text-base text-neutral-400 leading-relaxed">
              Regal Tuning operates a professional <Link to="/tuning-files" className="text-regal-amber underline-offset-4 hover:underline">ECU tuning file service</Link> for tuning businesses and workshops worldwide. FLEX Slave customers linked to Regal Tuning submit files straight to our calibration team — and FLEX Master users keep full independence, using us as their file provider by choice.
            </p>
          </Reveal>
          <Reveal delay={0.16} className="mt-8 flex flex-wrap items-center gap-4">
            <PortalButton label="Upload a File" testId="magicflex-upload-cta" />
            <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-regal-amber underline-offset-4 hover:underline" data-testid="magicflex-portal-link">
              Existing customer? File Portal <ArrowUpRight className="inline" size={13} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="magicflex-faq">
        <div className="container-regal max-w-4xl">
          <Reveal><p className="overline-label mb-4">FAQ</p></Reveal>
          <Reveal delay={0.06}><h2 className="mb-10 font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-ink leading-[1.08]">Magicmotorsport FLEX questions, answered</h2></Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="magicflex-faq-accordion">
              {FAQS.map(([q, a], i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-line" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-display text-sm sm:text-base font-semibold text-ink hover:text-regal-amber hover:no-underline">{q}</AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-neutral-400">{a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 text-sm text-neutral-400">
              Ready to order? Browse the <Link to="/tuning-tools" className="text-regal-amber underline-offset-4 hover:underline">full tuning tools range</Link>, go straight to the <Link to="/product/flex-master-full" className="text-regal-amber underline-offset-4 hover:underline">FLEX Full Master</Link>, or <Link to="/contact" className="text-regal-amber underline-offset-4 hover:underline">contact our team</Link> for advice on the right package.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}

export default function MagicFlex() {
  return (
    <main data-testid="magicflex-page">
      <MagicFlexContent />
    </main>
  );
}
